<template>
  <div class="gw-e-checklist generic-border" :class="classes">
    <div class="gw-e-checklist__header v-centerer">
      <EditableLabel
        v-model="checkList.name"
        class="gw-e-checklist__name"
        :legend="$t('message.NAME')"
        :placeholder="$t('message.CLICK_TO_TYPE')"
        :disabled="disabled"
        use-textarea
        @input="editName"
      />

      <div class="gw-e-checklist__count h-v-centerer">
        <span v-if="!$store.state.device.isSmartphoneLandscape">
          {{ $tc('message.GW_CL_ITEMS_COUNT', checkList.items.length, { count: checkList.items.length }) }}
        </span>
        <span v-else>
          {{ checkList.items.length }}
        </span>
      </div>

      <div class="gw-e-checklist__dup h-v-centerer">
        <button
          v-if="!noDelete"
          class="generic-border generic-border--icon-button generic-border--blue-border"
          @click="duplicateCheckList"
        >
          <Icon>{{ copyMedium }}</Icon>
        </button>
      </div>

      <div class="gw-e-checklist__delete h-v-centerer">
        <button
          v-if="!noDelete && !disabled"
          class="generic-border generic-border--icon-button generic-border--red"
          @click="deleteCheckList"
        >
          <Icon>{{ deleteMedium }}</Icon>
        </button>
      </div>
    </div>
    <div class="gw-e-checklist__expand" @click="expand()">
      <div class="gw-e-checklist__expand__button">
        <Icon :h="12">{{ wideArrowTopMedium }}</Icon>
      </div>
    </div>
    <TransitionExpand>
      <div v-if="isExpanded" class="gw-e-checklist__items">
        <Draggable
          v-model="checkList.items"
          :animation="200"
          ghost-class="drag-ghost"
          @input="onOrderChange"
        >
          <GwEditionChecklistItem
            v-for="(item, index) in checkList.items"
            :key="item.id"
            :item="item"
            :index="index"
            :disabled="disabled"
            @edit="expand(true)"
            @delete="itemDeleted(index)"
          />
        </Draggable>

        <button
          class=" generic-border generic-border--blue-border generic-border--text-btn"
          @click="addItem"
        >
          <div class="generic-border__left">
            <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
            <span>{{ $t('message.GW_CL_ADD_ITEM') }}</span>
          </div>
          <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
        </button>
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
/**
 * Editable checklist that allows the user to edit the name and add/remove items
 *
 * @vue-prop {Object} checkList - Checklist data as an object { name, items : [] }
 * @vue-prop {Boolean} noDelete - Whether the item can be deleted or not
 *
 * @vue-event {} delete - User confirmed the deletion of the checklist
 */

import toastr from 'toastr';
// Import components
import Draggable from 'vuedraggable';
import EditableLabel from '@/components/editable-label.vue';
import GwEditionChecklistItem from '@/components/gw-edition-checklist-item.vue';
import Icon from '@/components/icon.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Import utils
import { DUPLICATE_GW_CHECKLIST, EDIT_GW_CHECKLIST } from '@/store/modules/gemba-walks/action-types';
import { preventPastingStyles, calert } from '@/utils/main';
import {
  addExtraBold,
  arrowDownLine,
  copyMedium,
  deleteMedium,
  wideArrowTopMedium,
} from '@/assets/icons/';
import { GW_CHECKLIST_ITEM_TYPE } from '@/utils/gw-utils';

export default {

  name: 'GwEditionChecklist',

  components: {
    Draggable,
    EditableLabel,
    GwEditionChecklistItem,
    Icon,
    TransitionExpand,
  },

  props: {
    checkList: {
      type: Object,
      required: true,
    },
    noDelete: Boolean,
    disabled: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      copyMedium,
      deleteMedium,
      wideArrowTopMedium,
      isExpanded: false,
    };
  },

  computed: {
    classes() {
      return {
        expanded: this.isExpanded,
      };
    },
  },

  mounted() {
    preventPastingStyles();
  },


  methods: {
    expand(force) {
      this.isExpanded = !!force || !this.isExpanded;
    },
    duplicateCheckList() {
      this.$store.dispatch(`gembaWalks/${DUPLICATE_GW_CHECKLIST}`, { id: this.checkList.id });
    },
    /** Delete the entire checklist */
    deleteCheckList() {
      const buttons = [
        {
          sClass: 'red',
          title: this.$t('message.GW_CL_DELETE'),
          action: () => {
            this.$emit('delete');
          },
        },
        { title: this.$t('message.CANCEL') },
      ];
      if (this.checkList.items.some(item => item.type === GW_CHECKLIST_ITEM_TYPE.GW_INDICATOR)) {
        buttons.splice(0, 0, {
          title: this.$t('message.GW_CL_DELETE_ORPHAN'),
          sClass: 'red',
          action: () => {
            this.$emit('delete', { deleteOrphanIndicators: true });
          },
        });
      }

      calert(this.$t('message.GW_CL_CONFIRM_DELETE'), buttons);
    },
    editName() {
      this.$store.dispatch(`gembaWalks/${EDIT_GW_CHECKLIST}`, { id: this.checkList.id, name: this.checkList.name });
    },
    addItem() {
      this.App.post('/server/gembaWalks/checkLists/addItem.php', { id: this.checkList.id }, (d) => {
        if (d > 0) {
          toastr.success(this.$t('message.GW_SAVED'));
          this.checkList.items.push({ id: d, text: '', type: 0, files: [] });
          this.$nextTick(preventPastingStyles);
          this.expand(true);
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    itemDeleted(index) {
      toastr.success(this.$t('message.GW_ITEM_DELETED'));
      this.checkList.items.splice(index, 1);
      this.$nextTick(() => {
        this.expand(true);
      });
    },
    onOrderChange(newList) {
      const params = {
        id: this.checkList.id,
        items: newList.map(i => i.id),
      };
      this.App.post('/server/gembaWalks/checkLists/orderItems.php', params, (data) => {
        if (data > 0) {
          toastr.success(this.$t('message.GW_SAVED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/gw-item.scss";

.gw-e-checklist {
  padding: .7rem;

  &:not(:first-child) {
    margin-top: .7rem;
  }

  &__header {

  }

  &__name {
    flex: 1;
    min-width: 0px;
  }

  &__dup {
    flex: 0 0 5rem;
    margin: 0 1rem;
  }

  &__delete {
    flex: 0 0 5rem;
    margin: 0 1rem;
  }

  &__count {
    flex: 0 0 25rem;
    margin: 0 1rem;
  }

  &__expand {
    margin-top: .5rem;
    padding: .3rem;
    cursor: pointer;

    &:hover {
      background: $main-hover-color;
    }

    &__button {
      height: 1em;
      padding: 0.2em;
      text-align: center;
      transition: background 0.1s linear;
      .icon {
        height: 100% !important;
        transition: transform 0.1s linear;
        transform: rotate(180deg);
      }
    }
  }

  &.expanded .gw-e-checklist__expand__button .icon {
    transform: rotate(0deg);
  }

  &__items {
    margin-top: 1em;
    margin-left: 2em;
    position: relative;

    &:before {
      content: " ";
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: -1em;
      background: #bcbcbc;
      width: 0.2em;
    }
    .add-button {
      margin: .5em 0px;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .gw-e-checklist {
    &__count {
      flex: 0 0 5rem;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .gw-e-checklist {
    &__count, &__dup, &__delete {
      flex: 0 0 3rem;
    }
  }
}
</style>
